import { type ReactNode, useState } from 'react';
import { Outlet, useLocation } from 'react-router';
import PageContent from './shared/components/layout/PageContent';
import WdsPageHeader, { type BreadcrumbItem } from './shared/components/layout/PageHeader';
import Profile from './shared/components/layout/Profile';
import SideNavbar from './shared/components/layout/SideNavbar';
import * as Icons from './shared/components/icons';
import { useMediaQuery, useSessionStorage } from './shared/hooks';
import type { NavItemType } from './shared/types/navigation';

/** Item menu: `icon` = nama ikon WDS (mis. `Home`). */
export interface ShellNavItem {
  to: string;
  label: string;
  icon?: string;
}
export interface ShellNavGroup {
  label: string;
  icon?: string;
  items: ShellNavItem[];
}
export type ShellNavEntry = ShellNavItem | ShellNavGroup;
const isGroup = (e: ShellNavEntry): e is ShellNavGroup => 'items' in e;

type IconCmp = (props: { className?: string }) => ReactNode;
/** Ikon menu: nama ikon WDS yang tersedia di `core.tsx` (tambahkan di sana bila butuh ikon lain). */
const icon = (name?: string) => {
  const C = ((name && (Icons as unknown as Record<string, IconCmp>)[name]) ||
    Icons.Widget) as IconCmp;
  return <C />;
};

function toNav(entries: ShellNavEntry[]): NavItemType[] {
  return entries.map((e) =>
    isGroup(e)
      ? {
          label: e.label,
          icon: icon(e.icon),
          children: e.items.map((i) => ({ label: i.label, path: i.to })),
        }
      : { label: e.label, path: e.to, icon: icon(e.icon) },
  );
}

/**
 * Kerangka aplikasi WDS (sidebar + header + konten) untuk BrowserRouter: judul & breadcrumb diturunkan dari menu aktif.
 * `banners` tampil di atas header (pengumuman, masa uji coba, dll.).
 */
export function AppShell({
  appName,
  logo,
  collapsedLogo,
  nav,
  user,
  onLogout,
  settingsPath,
  banners,
  headerExtra,
}: {
  appName: string;
  logo?: ReactNode;
  collapsedLogo?: ReactNode;
  nav: ShellNavEntry[];
  user: { name: string; roleName?: string } | null;
  onLogout?: () => void;
  settingsPath?: string;
  banners?: ReactNode;
  headerExtra?: ReactNode;
}) {
  const { pathname } = useLocation();
  const narrow = useMediaQuery('(max-width: 1023px)');
  const [stored, setStored] = useSessionStorage<boolean | null>('sidebar-collapsed', null);
  const collapsed = stored ?? narrow;
  const [mobileOpen, setMobileOpen] = useState(false);

  const on = (to: string) => pathname === to || pathname.startsWith(`${to}/`);
  let title = appName;
  const crumbs: BreadcrumbItem[] = [];
  for (const e of nav) {
    if (isGroup(e)) {
      const hit = e.items.find((i) => on(i.to));
      if (hit) {
        title = hit.label;
        crumbs.push({ label: e.label }, { label: hit.label });
        break;
      }
    } else if (on(e.to)) {
      title = e.label;
      crumbs.push({ label: e.label });
      break;
    }
  }

  return (
    <div className="flex h-screen overflow-hidden">
      <SideNavbar
        items={toNav(nav)}
        collapsed={collapsed}
        mobileOpen={mobileOpen}
        onToggle={() => setStored(!collapsed)}
        onMobileClose={() => setMobileOpen(false)}
        logo={logo}
        collapsedLogo={collapsedLogo}
        appName={appName}
      />
      <div
        id="main-content"
        className={`relative flex min-w-0 flex-1 flex-col bg-surface transition-all duration-300 ${collapsed ? 'md:ml-16' : 'md:ml-64'}`}
      >
        {banners}
        <WdsPageHeader
          title={title}
          breadcrumb={crumbs.length > 1 ? crumbs : undefined}
          onMenuToggle={() => setMobileOpen(!mobileOpen)}
        >
          <div className="flex items-center gap-2">
            {headerExtra}
            <Profile user={user} onLogout={onLogout} settingsPath={settingsPath} />
          </div>
        </WdsPageHeader>
        <PageContent>
          <Outlet />
        </PageContent>
      </div>
    </div>
  );
}
